import { createContext, useCallback, useContext, useState, type ReactNode } from "react";

export interface ToastItem {
  id: number;
  message: string;
  kind: "success" | "error";
}

const ToastContext = createContext<{ toast: (message: string, kind?: "success" | "error") => void } | null>(null);
let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const toast = useCallback((message: string, kind: "success" | "error" = "success") => {
    const id = nextId++;
    setItems((prev) => [...prev, { id, message, kind }]);
    setTimeout(() => setItems((prev) => prev.filter((i) => i.id !== id)), 4200);
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="fixed top-4 right-4 z-[70] space-y-2">
        {items.map((item) => (
          <div
            key={item.id}
            className={`fade-in flex items-start gap-2 border rounded-lg shadow-soft px-4 py-3 text-sm max-w-sm ${
              item.kind === "error"
                ? "border-red-300 bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300"
                : "border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
            }`}
          >
            <span>{item.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx.toast;
}
